import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BookOpen, Play, Square } from "lucide-react";

import { AnimalFigure } from "@/components/camp/AnimalFigure";
import type { AnimalId } from "@/components/camp/campData";
import { cn } from "@/engine/support/classNames";

import { LandingSection } from "./LandingSection";
import { narratorClip, type NarratorLine } from "./narratorClip";

/**
 * The narrator, heard (#949).
 *
 * In a room the narrator reads aloud whatever the host gives it, from a seat of
 * its own at the fire. Here a visitor picks one of three lines and hears the
 * narrator's own voices read it, the words lighting up as they are said, while
 * three listeners react at the end.
 *
 * The recordings were made once by `scripts/landing-narrator-clips.mjs`, in
 * every locale, so a stranger opening the page never spends the provider's
 * quota; nothing is fetched until a line is pressed, and nothing plays unasked.
 */

const LINES = [
  "letter",
  "mafia",
  "toast",
] as const satisfies readonly NarratorLine[];
type Line = NarratorLine;

/** How each line lands with the listeners when it is done. */
const REACTION: Record<Line, string> = {
  letter: "🔥",
  mafia: "😂",
  toast: "🥂",
};

const LISTENERS: readonly AnimalId[] = ["fox", "bear", "hare"];

/** A guess at pace for when no recording answers, so the words still move. */
const MS_PER_CHARACTER = 65;

function useReading() {
  const { i18n } = useTranslation();
  const [line, setLine] = useState<Line | null>(null);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState<Line | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  const frame = useRef<number | null>(null);

  const stop = () => {
    audio.current?.pause();
    audio.current = null;
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
    setLine(null);
  };

  useEffect(() => stop, []);

  const read = (next: Line, words: string) => {
    stop();
    setDone(null);
    setProgress(0);
    setLine(next);
    const clip = new Audio(narratorClip(i18n.resolvedLanguage, next));
    audio.current = clip;
    const started = performance.now();
    const estimate = words.length * MS_PER_CHARACTER;
    const tick = () => {
      const known = Number.isFinite(clip.duration) && clip.duration > 0;
      const ratio = known
        ? clip.currentTime / clip.duration
        : (performance.now() - started) / estimate;
      setProgress(Math.min(1, ratio));
      if (ratio >= 1 || (known && clip.ended)) {
        frame.current = null;
        setLine(null);
        setDone(next);
        return;
      }
      frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    // A missing or refused recording still reads at a guessed pace.
    void clip.play().catch(() => {});
  };

  return { line, progress, done, read, stop };
}

function Scroll({
  text,
  progress,
  active,
}: {
  text: string;
  progress: number;
  active: boolean;
}) {
  const words = text.split(" ");
  const lit = Math.round(progress * words.length);
  return (
    <p
      aria-live="polite"
      className="relative rounded-2xl border border-border bg-background/70 p-4 text-base leading-relaxed backdrop-blur-sm"
    >
      {words.map((word, index) => (
        <span
          key={`${word}-${index}`}
          className={cn(
            "transition-colors duration-(--motion-quick)",
            active && index < lit ? "text-primary" : "text-muted-foreground",
          )}
        >
          {word}{" "}
        </span>
      ))}
    </p>
  );
}

export function LandingNarrator() {
  const { t } = useTranslation();
  const reading = useReading();
  const [chosen, setChosen] = useState<Line>("letter");
  const shown = reading.line ?? chosen;
  const text = t(`landing.narrator.lines.${shown}.text`);

  return (
    <LandingSection
      id="narrator"
      eyebrow={t("landing.narrator.eyebrow")}
      title={t("landing.narrator.title")}
      lead={t("landing.narrator.body")}
    >
      <div className="grid items-center gap-8 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <ul className="grid gap-2.5">
          {LINES.map((line) => {
            const playing = reading.line === line;
            return (
              <li key={line}>
                <button
                  type="button"
                  aria-pressed={playing}
                  data-testid={`landing-narrator-${line}`}
                  onClick={() => {
                    setChosen(line);
                    if (playing) reading.stop();
                    else
                      reading.read(
                        line,
                        t(`landing.narrator.lines.${line}.text`),
                      );
                  }}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-2xl border px-4 py-3.5 text-left transition hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    chosen === line
                      ? "border-primary/50 bg-primary/10"
                      : "border-border bg-card",
                  )}
                >
                  <span className="grid size-9 flex-none place-items-center rounded-xl bg-primary/15 text-primary">
                    {playing ? (
                      <Square className="size-4" aria-hidden="true" />
                    ) : (
                      <Play className="size-4" aria-hidden="true" />
                    )}
                  </span>
                  <span className="flex-1 font-semibold">
                    {t(`landing.narrator.lines.${line}.title`)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {playing
                      ? t("landing.narrator.reading")
                      : t("landing.narrator.play")}
                  </span>
                </button>
              </li>
            );
          })}
          <li className="pt-2 text-sm text-muted-foreground">
            {t("landing.narrator.note")}
          </li>
        </ul>

        <div
          data-testid="landing-narrator-stage"
          className="relative overflow-hidden rounded-3xl border border-border bg-[radial-gradient(60%_50%_at_50%_100%,hsl(var(--primary)/0.22),transparent_70%),linear-gradient(to_bottom,hsl(var(--background)),hsl(var(--card)))] p-5"
        >
          <div className="flex items-start gap-4">
            <div className="relative flex-none">
              <AnimalFigure
                aria-hidden="true"
                animal="owl"
                pose={reading.line ? "talk" : "stand"}
                className="!h-28 !w-20"
              />
              <span className="absolute -right-2 bottom-2 grid size-8 place-items-center rounded-full bg-primary text-primary-foreground shadow-glow-sm">
                <BookOpen className="size-4" aria-hidden="true" />
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <Scroll
                text={text}
                progress={reading.progress}
                active={reading.line !== null}
              />
            </div>
          </div>
          <div
            aria-hidden="true"
            className="mt-4 flex items-end justify-center gap-6"
          >
            {LISTENERS.map((animal, index) => (
              <div key={animal} className="relative">
                {reading.done ? (
                  <span
                    className="landing-rise absolute -top-2 left-1/2 text-lg"
                    style={{ animationDelay: `${index * 120}ms` }}
                  >
                    {REACTION[reading.done]}
                  </span>
                ) : null}
                <AnimalFigure animal={animal} className="!h-16 !w-12" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </LandingSection>
  );
}
